import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Upload, Search, Trash2, Tag, Image as ImageIcon } from 'lucide-react';
import AppHeader from '../components/AppHeader';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
};

const DesignLibrary = () => {
  const { user, logout } = useContext(AuthContext);
  const [designs, setDesigns] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState({ name: '', customer_name: '', customer_email: '', decoration_notes: '', print_method: 'embroidery', tags: '' });
  const [imageBase64, setImageBase64] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Detail dialog: shows the full image + metadata when a tile is clicked.
  const [detail, setDetail] = useState(null);

  const fetchDesigns = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/designs', { params: search ? { search } : {} });
      setDesigns(r.data || []);
    } catch (e) {
      toast.error('Failed to load designs');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchDesigns(); }, [search]); // eslint-disable-line

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setImageBase64(ev.target.result); };
    reader.readAsDataURL(f);
  };

  const submitDesign = async () => {
    if (!form.name || !imageBase64) { toast.error('Name and image are required'); return; }
    setUploading(true);
    try {
      await axios.post('/designs', {
        name: form.name,
        image_base64: imageBase64,
        customer_email: form.customer_email || null,
        customer_name: form.customer_name || null,
        decoration_notes: form.decoration_notes || null,
        print_method: form.print_method || null,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      });
      toast.success('Design saved to library');
      setUploadOpen(false);
      setForm({ name: '', customer_name: '', customer_email: '', decoration_notes: '', print_method: 'embroidery', tags: '' });
      setImageBase64(null);
      fetchDesigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const deleteDesign = async (id) => {
    if (!window.confirm('Delete this design from the library?')) return;
    try {
      await axios.delete(`/designs/${id}`);
      toast.success('Design deleted');
      fetchDesigns();
      setDetail(null);
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const openDetail = async (id) => {
    try {
      const r = await axios.get(`/designs/${id}`);
      setDetail(r.data);
    } catch { toast.error('Could not load design'); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <AppHeader user={user} onLogout={logout} />
      <main style={{ padding: '32px', maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, color: '#0f172a' }}>Design Library</h1>
            <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>
              Reusable artwork bound to customers. Drop in any past panther head, logo, or graphic — pull onto any future proposal in one click.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, customer…"
                style={{ paddingLeft: 32, width: 260 }}
                data-testid="design-search"
              />
            </div>
            <Button onClick={() => setUploadOpen(true)} data-testid="upload-design-btn" style={{ background: '#7c3aed', color: '#fff' }}>
              <Upload size={14} style={{ marginRight: 6 }} />Upload Design
            </Button>
          </div>
        </div>

        {loading ? (
          <p style={{ color: '#64748b' }}>Loading…</p>
        ) : designs.length === 0 ? (
          <div style={{ background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 12, padding: 48, textAlign: 'center' }}>
            <ImageIcon size={32} color="#cbd5e1" style={{ margin: '0 auto 8px' }} />
            <p style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', margin: 0 }}>No designs yet</p>
            <p style={{ fontSize: 13, color: '#64748b', margin: '6px 0 0' }}>Upload your first design to build the library.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {designs.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => openDetail(d.id)}
                data-testid={`design-tile-${d.id}`}
                style={{
                  all: 'unset', cursor: 'pointer',
                  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 10, textAlign: 'left',
                }}
              >
                <div style={{
                  width: '100%', paddingBottom: '100%', background: '#f8fafc', borderRadius: 8,
                  display: 'grid', placeItems: 'center', color: '#cbd5e1', fontSize: 11,
                }}>
                  No preview
                </div>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', margin: '8px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</p>
                <p style={{ fontSize: 11, color: '#64748b', margin: '2px 0 0' }}>{d.customer_name || 'Unassigned'}</p>
                <p style={{ fontSize: 10, color: '#94a3b8', margin: '4px 0 0' }}>
                  {d.print_method ? d.print_method.replace('_', ' ').toUpperCase() : '—'} · {d.used_in_proposal_ids?.length || 0} uses · Added {fmtDate(d.created_at)}
                </p>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent style={{ maxWidth: 540 }}>
          <DialogHeader>
            <DialogTitle>Upload Design</DialogTitle>
          </DialogHeader>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <Label>Image *</Label>
              <Input type="file" accept="image/*" onChange={handleFile} data-testid="design-file-input" />
              {imageBase64 && <img src={imageBase64} alt="" style={{ marginTop: 8, maxHeight: 180, borderRadius: 8, background: '#f1f5f9' }} />}
            </div>
            <div><Label>Design name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="design-name" /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div><Label>Customer name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
              <div><Label>Customer email</Label><Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
            </div>
            <div><Label>Decoration / production notes</Label><Input value={form.decoration_notes} onChange={(e) => setForm({ ...form, decoration_notes: e.target.value })} placeholder="Threads, stitch count, etc." /></div>
            <div><Label>Tags</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="comma, separated" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
            <Button onClick={submitDesign} disabled={uploading} data-testid="design-save" style={{ background: '#7c3aed', color: '#fff' }}>
              {uploading ? 'Saving…' : 'Save to Library'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent style={{ maxWidth: 640 }}>
          <DialogHeader>
            <DialogTitle>{detail?.name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div>
              <img src={detail.image_base64?.startsWith('data:') ? detail.image_base64 : `data:image/png;base64,${detail.image_base64}`}
                alt={detail.name}
                style={{ width: '100%', maxHeight: 360, objectFit: 'contain', background: '#f1f5f9', borderRadius: 8 }} />
              <div style={{ marginTop: 12, fontSize: 13, color: '#475569', display: 'grid', gap: 6 }}>
                <div><strong>Customer:</strong> {detail.customer_name || 'Unassigned'} {detail.customer_email && <span style={{ color: '#7c3aed' }}>· {detail.customer_email}</span>}</div>
                <div><strong>Method:</strong> {detail.print_method?.replace('_', ' ') || '—'}</div>
                {detail.decoration_notes && <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: 8 }}><strong style={{ color: '#92400e' }}>NOTES:</strong> {detail.decoration_notes}</div>}
                {detail.tags?.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}><Tag size={12} />{detail.tags.map((t) => <span key={t} style={{ fontSize: 11, padding: '2px 7px', background: '#ede9fe', color: '#6d28d9', borderRadius: 999 }}>{t}</span>)}</div>}
                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                  Used on {detail.used_in_proposal_ids?.length || 0} proposal{detail.used_in_proposal_ids?.length === 1 ? '' : 's'} · Added {fmtDate(detail.created_at)} {detail.created_by && `by ${detail.created_by}`}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => deleteDesign(detail?.id)} style={{ color: '#dc2626', borderColor: '#fca5a5' }}>
              <Trash2 size={14} style={{ marginRight: 4 }} />Delete
            </Button>
            <Button onClick={() => setDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DesignLibrary;
